import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Ban, Loader2, Mail, AlertTriangle } from 'lucide-react';

export const DEFAULT_CANCELLATION_REASONS = [
  'Customer requested',
  'Weather',
  'Equipment unavailable',
  'Operator unavailable',
  'Site access issue',
  'Scheduling conflict',
  'Other',
];

export const CancelJobDialog = ({
  open,
  onOpenChange,
  job,
  customerEmail,
  reasons,
  onConfirm,
  submitting,
}) => {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(false);

  const reasonList = (reasons && reasons.length) ? reasons : DEFAULT_CANCELLATION_REASONS;

  useEffect(() => {
    if (open) {
      setReason('');
      setNotes('');
      setSendEmail(false);
    }
  }, [open]);

  const handleConfirm = () => {
    if (!reason) return;
    onConfirm({ reason, notes: notes.trim() || null, send_email: sendEmail });
  };

  const jobLabel = job ? `#${job.job_number}` : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cancel-job-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-zinc-400" />
            Cancel Job {jobLabel}
          </DialogTitle>
          <DialogDescription>
            The job stays on the board marked as Cancelled. All assets and workers will be de-allocated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Reason <span className="text-red-500">*</span></Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="cancel-reason" data-testid="cancel-reason-select">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {reasonList.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`cancel-reason-${r.replace(/\s+/g, '-').toLowerCase()}`}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancel-notes">Notes (optional)</Label>
            <Textarea
              id="cancel-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional context…"
              rows={3}
              data-testid="cancel-notes-input"
            />
          </div>

          <div className="rounded-lg border p-3 space-y-2">
            <div className="flex items-start gap-3">
              <Checkbox
                id="cancel-send-email"
                checked={sendEmail}
                onCheckedChange={(v) => setSendEmail(!!v)}
                disabled={!customerEmail}
                data-testid="cancel-send-email-checkbox"
              />
              <div className="flex-1 min-w-0">
                <Label htmlFor="cancel-send-email" className="flex items-center gap-1.5 cursor-pointer">
                  <Mail className="h-3.5 w-3.5" /> Send cancellation email to customer
                </Label>
                {customerEmail ? (
                  <p className="text-xs text-muted-foreground truncate mt-1">{customerEmail}</p>
                ) : (
                  <p className="text-xs text-amber-500 flex items-center gap-1 mt-1">
                    <AlertTriangle className="h-3 w-3" /> No customer email on file
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Keep Job
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!reason || submitting}
            className="bg-zinc-700 hover:bg-zinc-800 text-white"
            data-testid="confirm-cancel-job-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
            Cancel Job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelJobDialog;
